import { AuthService } from 'src/app/services/auth.service';
import { Component, ElementRef, EventEmitter, Input, OnInit, Output, SimpleChange, ViewChild, AfterViewInit } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { finalize, take } from 'rxjs/operators';
import { OffersService } from '../../../services/resources/offers.service';
import { ExtendService } from 'src/app/services/resources/extend.service';

@Component({
  selector: 'app-flex-renew-modal',
  templateUrl: './flex-renew-modal.component.html',
  styleUrls: ['./flex-renew-modal.component.scss']
})
export class FlexRenewModalComponent implements OnInit, AfterViewInit {

  modalName = 'FlexrenewModal';
  isLoading: boolean = false;
  isSending: boolean = false;
  currentUUID: string = '';

  @Input() isActive;
  @Input() selectedDaysIndex: number = 0;
  @Input() currentQuotaDescription;
  @Input() userBalance;
  @Input() profileId;

  @Output() isClosed = new EventEmitter<string>();
  @Output() refreshParent = new EventEmitter<boolean>();

  @ViewChild('modalContent') modalContent: ElementRef;
  @ViewChild('closeButton') closeButton: ElementRef;
  @ViewChild('quotaButtons') quotaButtons: ElementRef;
  @ViewChild('daysButtons') daysButtons: ElementRef;

  offers: any[] = [];
  quotaOffers: any[] = [{ id: null, price: 0 }];
  daysOffers: any[] = [{ id: null, price: 0 }];
  selectedQuota: any = this.quotaOffers[0];
  selectedDays: any = this.daysOffers[0];
  totalPrice: number = 0;
  step: number = 1;
  selectedQuotaIndex: number = 0;
  isScrollable: boolean = false;

  constructor(
    private toastrService: ToastrService,
    private authService: AuthService,
    private offersService: OffersService,
    private extendService: ExtendService
  ) {}

  ngOnInit(): void {}

  ngAfterViewInit(): void {
    // no-op: scrolling handled when modal is opened (ngOnChanges)
  }

  checkIfClickedInside = (event: Event) => {
    let isClickInside = this.modalContent.nativeElement.contains(event.target);
    if (!isClickInside) {
      this.closeModal();
    }
  };

  changeStatusToClosed(modalName: string) {
    this.isClosed.emit(modalName);
  }

  getOffers() {
    this.isLoading = true;
    this.offersService.getSpecific(this.profileId)
      .pipe(take(1), finalize(() => this.isLoading = false))
      .subscribe(
        res => {
          this.offers = res['data'] || [];
          this.categorizeOffers();
        },
        err => {
          console.error('Error fetching offers:', err);
        }
      );
  }

  categorizeOffers() {
    this.quotaOffers = this.offers
      .map(o => {
        try {
          const desc = JSON.parse(o.description);
          if (desc.type === 'flex' && desc.sub_type === 'qouta') {
            return {
                ...o,
                descriptionParsed: desc
              };
          }
        } catch (e) {
          console.error('Invalid JSON in quota offer:', o);
        }
        return null;
      })
      .filter(o => o !== null)
      .sort((a, b) => (a.id || 0) - (b.id || 0));

    this.daysOffers = this.offers
      .map(o => {
        try {
          const desc = JSON.parse(o.description);
          if (desc.type === 'flex' && desc.sub_type === 'exp') {
            return {
                ...o,
                descriptionParsed: desc
              };
          }
        } catch (e) {
          console.error('Invalid JSON in days offer:', o);
        }
        return null;
      })
      .filter(o => o !== null)
      .sort((a, b) => (a.id || 0) - (b.id || 0));

    this.selectedQuotaIndex = -1;
    this.selectedQuota = null;
    this.selectedDaysIndex = -1;
    this.selectedDays = null;
  }

  updateSelection(type: string, eventOrIndex: Event | number) {
    let index: number;
    if (typeof eventOrIndex === 'number') {
      index = eventOrIndex;
    } else {
      const inputElement = eventOrIndex.target as HTMLInputElement;
      index = parseInt(inputElement.value, 10);
    }

    if (type === 'quota') {
      if (this.selectedQuotaIndex === index) {
        this.selectedQuotaIndex = -1;
        this.selectedQuota = null;
      } else {
        this.selectedQuotaIndex = index;
        this.selectedQuota = this.quotaOffers[index];
      }
    } else if (type === 'days') {
      if (this.selectedDaysIndex === index) {
        this.selectedDaysIndex = -1;
        this.selectedDays = null;
      } else {
        this.selectedDaysIndex = index;
        this.selectedDays = this.daysOffers[index];
      }
    }

    this.calculateTotal();
  }

  calculateTotal() {
    this.totalPrice = (this.selectedQuota?.price || 0) + (this.selectedDays?.price || 0);
  }

  changeStep(step: number) {
    this.step = step;
  }

  async activateOffer(): Promise<void> {
    this.isLoading = true;

    let offersToActivate = [];
    if (this.selectedQuota?.id) offersToActivate.push(this.selectedQuota);
    if (this.selectedDays?.id) offersToActivate.push(this.selectedDays);

    if (offersToActivate.length === 0) {
      this.toastrService.warning("لم يتم اختيار أي عرض للتفعيل.");
      this.isLoading = false;
      return;
    }

    let userPassword = this.getUserPassword();
    if (userPassword === null) {
      this.isLoading = false;
      return;
    }

    for (let offer of offersToActivate) {
      const desc = offer.descriptionParsed;
      const offerSummary = desc.sub_type === 'qouta'
        ? `${desc.quota} ${desc.quota_unit}`
        : `${desc.exp} ${desc.exp_unit}`;

      let data = { profile_id: offer.id, current_password: userPassword };

      try {
        const response = await this.extendService.post(data).pipe(take(1)).toPromise();
        if (response['status'] === 200) {
          this.toastrService.success(`تم تفعيل العرض: ${offerSummary} بنجاح!`);
        } else {
          this.handleActivationError(response, offerSummary);
        }
      } catch (error) {
        this.toastrService.error(`فشل تفعيل العرض: ${offerSummary}. الرجاء المحاولة لاحقًا.`);
      }
    }

    this.isLoading = false;
    this.authService.userPassword = userPassword;
    this.closeModal();
    this.refreshDashboard();
  }

  getUserPassword(): string | null {
    if (!this.authService.checkIfCameFromAutoLogin()) return 'true';
    return this.authService.userPassword || prompt("الرجاء إدخال كلمة المرور لإتمام الإجراء") || null;
  }

  handleActivationError(response: any, offerSummary: string) {
    if (response['status'] === -1) {
      if (response['message'] === 'rsp_insufficient_balance') {
        this.toastrService.error(`لا يمكنك تفعيل العرض: ${offerSummary}، الرصيد غير كافي!`);
        return;
      }
      if (response['message'] === 'rsp_invalid_password') {
        this.toastrService.error("كلمة المرور غير صحيحة، الرجاء المحاولة مجددًا!");
        return;
      }
    }
    this.toastrService.error(`حدث خطأ أثناء تفعيل العرض: ${offerSummary}. يرجى المحاولة لاحقًا.`);
  }

  closeModal() {
    this.isClosed.emit(this.modalName);
    setTimeout(() => {
      this.step = 1;
      this.selectedQuotaIndex = -1;
      this.selectedDaysIndex = -1;
      this.selectedQuota = null;
      this.selectedDays = null;
      this.totalPrice = 0;
      this.closeButton.nativeElement.click();
      this.changeStatusToClosed(this.modalName);
      document.removeEventListener("click", this.checkIfClickedInside, true);
    }, 500);
  }

  refreshDashboard() {
    this.refreshParent.emit(true);
  }

  checkScrollable() {
    try {
      const quotaEl = this.quotaButtons?.nativeElement;
      const daysEl = this.daysButtons?.nativeElement;
      this.isScrollable = (quotaEl && quotaEl.scrollWidth > quotaEl.clientWidth) || (daysEl && daysEl.scrollWidth > daysEl.clientWidth);
    } catch (e) {
      this.isScrollable = false;
    }
  }

  ngOnChanges(changes: { [property: string]: SimpleChange }) {
    this.getOffers();
    // when modal becomes active (opened), auto-scroll slightly on small screens
    if (changes['isActive']) {
      const change: SimpleChange = changes['isActive'];
      if (!change.isFirstChange() && change.currentValue) {
        // small delay to allow DOM to render buttons
        setTimeout(() => {
          this.checkScrollable();
          try {
            if (window.innerWidth < 768) {
              this.quotaButtons?.nativeElement.scrollBy({ left: 80, behavior: 'smooth' });
              this.daysButtons?.nativeElement.scrollBy({ left: 80, behavior: 'smooth' });
            }
          } catch (e) {
            // ignore if elements not ready
          }
        }, 300);
      }
    }
  }
}
